import React, { useCallback, useEffect, useState, useRef } from 'react'
import { useFormik } from 'formik'
import { isEmpty, intersection, mergeLeft } from 'ramda'
import { useHistory } from 'react-router-dom'
import SearchIcon from '@material-ui/icons/Search'
import { useDispatch, useSelector } from 'react-redux'
import { FormattedMessage, useIntl } from 'react-intl'
import { v4 as uuidv4 } from 'uuid'
import {
  Button,
  useTheme,
  Typography,
  makeStyles,
  FormControl,
  useMediaQuery,
  InputAdornment,
} from '@material-ui/core'

import { useGroups } from 'commons/useGroups'
import { useAppName } from 'commons/usePartner'
import useFeatureFlag from 'app/configs/useFeatureFlag'

import { WARNING } from 'app/constants/button'
import { SECONDARY } from 'app/constants/button'
import AccessControl from 'app/auth/AccessControl'
import { SCOPE_BEACON } from 'app/constants/scope'
import { DISTRESS } from 'app/constants/statusColors'
import { filterChannels } from 'app/constants/channel'
import { ACTION_NEW, GENERAL_DIALOG } from 'app/constants/compose'
import { AccessType } from 'app/auth/accessControlService'
import { Routes } from 'app/services/routeService/routeService'
import { openDialog } from 'app/auth/store/actions/createNotification.actions'
import {
  filterStatus,
  filterSource,
  filterContactType,
  STATUS_FILTER,
  SOURCE_FILTER,
  CHANNELS_FILTER,
  CONTACT_TYPE_FILTER,
  ZIP_CODE_FILTER,
  SubscriberMenuAction,
  ACTION_SOURCE_ROW,
  ACTION_SOURCE_CHECKBOX,
  CONTACT_TYPE_INVALID,
  STATUS_SUBSCRIBED_ACCEPT_REQUEST,
  STATUS_SUBSCRIBED_ACCEPT_INVITATION,
  STATUS_SUBSCRIBED_PUBLIC_SUBSCRIBE,
} from 'app/constants/subscriberCRM'

import { AddSubscribers } from 'app/components/icons'
import MessageAlert from 'app/components/MessageAlert'

import EmptyState from 'app/componentV4/EmptyState'
import PanelsInfo from 'app/componentV4/Panels/Info'
import PanelPages from 'app/componentV4/Panels/Pages'
import DataTable from 'app/componentV4/Table/DataTable'
import PanelFilter from 'app/componentV4/Panels/Filter'
import SimpleInput from 'app/componentV4/Input/SimpleInput'
import GroupsSelector from 'app/componentV4/Filters/GroupsSelector'
import OutlinedButtons from 'app/componentV4/Button/OutlinedButtons'
import InformationDialog from 'app/componentV4/Dialog/InformationDialog'
import CheckboxSelectorV2 from 'app/componentV4/Filters/CheckboxSelectorV2'
import ComposeMessageButton from 'app/componentV4/Button/ComposeMessageButton'

import useSubscribers from './useSubscribers'
import { SUBSCRIBER_GROUPS_QUERY_KEY } from './queries/useSubscriberGroupsQuery'
import { ColumnOfData } from './ColumnOfData'
import AutoApprove from './actions/AutoApprove'
import AddOneByOne from './AddSubscibers/AddOneByOne/AddOneByOne'
import MenusAddSubscriber from './AddSubscibers/MenusAddSubscriber'
import {
  PLACE_INVITATION_ACCEPT_REQUEST,
  PLACE_INVITATION_ADDED,
  PLACE_INVITATION_DECLINED,
  PLACE_INVITATION_INVITED,
  PLACE_INVITATION_PENDING_REQUEST,
  PLACE_INVITATION_UNVERIFIED,
} from 'app/configs/statusConfigs'
import BulkAssignGroupDialog from './BulkAssignGroupDialog'
import SelectAllSubscriberButton from './SelectAllSubscriberButton'
import BulkActionsButtonGroup from './BulkActionsButtonGroup'
import { usePlace } from 'commons/usePlace'
import { useFlag } from '@unleash/proxy-client-react'
import { useQueryClient } from '@tanstack/react-query'
import { SUBSCRIBERS } from 'app/constants/recipient'
import { SELECT_MODE } from 'app/constants/table'
import effects from 'app/effects/effects'
import { buildBulkDeletePayload } from './bulkActionHelpers'

const useStyles = makeStyles((theme) => ({
  table: {
    '&.data-table': {
      [theme.breakpoints.up('md')]: {
        // Base offset (200px) already includes ~50px for single-line filter
        // Add the difference when filter wraps: (filterHeight - 50)
        height: (props) => `calc(100vh - ${150 + props.filterHeight}px)`,
      },
    },
  },
  tableWithInfo: {
    '&.data-table': {
      [theme.breakpoints.up('md')]: {
        // Base offset (150px) already includes ~50px for single-line filter
        // filterHeight includes both filter and info panel when present
        height: (props) => `calc(100vh - ${150 + props.filterHeight}px)`,
      },
    },
  },
  infoDialog: {
    '& .MuiDialog-paper': {
      width: '475px !important',
    },
  },
}))

const invitedStatus = [PLACE_INVITATION_ADDED, PLACE_INVITATION_INVITED]
const pendingApprove = [PLACE_INVITATION_PENDING_REQUEST]

const Subscribers = () => {
  const theme = useTheme()
  const place = usePlace()
  const history = useHistory()
  const appName = useAppName()
  const dispatch = useDispatch()
  const { formatMessage } = useIntl()
  const isDownXs = useMediaQuery(theme.breakpoints.down('sm'))
  const queryClient = useQueryClient()
  const groupList = useSelector((state) => state.groups.groupList)
  const headerRef = useRef(null)
  const [filterHeight, setFilterHeight] = useState(50)
  const [isInfo, setIsInfo] = useState(false)
  const [selectedRow, setSelectedRow] = useState(null)
  const [selectedIds, setSelectedIds] = useState({})
  const [isReadMore, setIsReadMore] = useState(false)
  const [isOpenOneByOne, setIsOpenOneByOne] = useState(false)
  const [isOpenAddDialog, setIsOpenAddDialog] = useState(false)
  const [isResetSelected, setIsResetSelected] = useState(false)
  const [isResetAllSelected, setIsResetAllSelected] = useState(false)
  const [multipleActionItems, setMultipleActionItems] = useState({
    invited: [],
    pendingApprove: [],
  })
  const [isAddAnotherChecked, setIsAddAnotherChecked] = useState(false)
  const [isSendInvitationChecked, setIsSendInvitationChecked] = useState(false)
  const [isBulkAssignGroupDialogOpen, setIsBulkAssignGroupDialogOpen] =
    useState(false)
  const [dialogMode, setDialogMode] = useState(SELECT_MODE.SELECTED) // SELECT_MODE.SELECTED | SELECT_MODE.ALL
  const [idempotencyKey, setIdempotencyKey] = useState(uuidv4())
  const { mes_3841_show_crm_export } = useFeatureFlag()
  const mes_5173_crm_bulk_delete_subscribers = useFlag(
    'mes_5173_crm_bulk_delete_subscribers',
  )
  const enableFilterByZipCode = useFlag('mes_5156_display_zip_code_filter')

  const classes = useStyles({ filterHeight })

  const filterZipCode = [
    {
      id: 1,
      name: 'zipCode',
      label: 'filter.quickfilter.zip.code',
      value: 'true',
    },
  ]

  const resetSubscriberSelection = () => {
    setSelectedIds({})
    setSelectedRow(null)
    setIsResetAllSelected(true)
    setMultipleActionItems({ invited: [], pendingApprove: [] })
  }

  const openBulkAssignGroupDialog = () => {
    setIdempotencyKey(uuidv4())
    setIsBulkAssignGroupDialogOpen(true)
  }

  const closeDialog = () => {
    setIsBulkAssignGroupDialogOpen(false)
    // Keep dialogMode as-is, don't reset to null
  }

  const handleToggleSelectAll = () => {
    if (dialogMode === SELECT_MODE.ALL) {
      setDialogMode(SELECT_MODE.SELECTED)
      resetSubscriberSelection()
      setIsBulkAssignGroupDialogOpen(false)
    } else {
      setDialogMode(SELECT_MODE.ALL)
    }
  }

  // Unified API call handler for assigning groups (works for both modes)
  const handleBulkAssignGroups = async (payload) => {
    const { action, group, target } = payload

    const apiPayload = {
      placeId: place.id,
      action,
      group: {
        create: group.create
          ? {
              name: group.create.name,
              subgroups: group.create.subgroups,
            }
          : null,
        existing: group.existing
          ? {
              groupIds: group.existing.groupIds,
              subgroupIds: group.existing.subgroupIds,
            }
          : null,
      },
      target: {
        type: target.type,
        value: target.value,
      },
    }

    try {
      const response = await effects.apis.bulkAssignSubscribersToGroups(
        apiPayload,
        {
          'x-idempotency-key': idempotencyKey,
        },
      )

      // Refresh group list
      await fetchAllGroups()

      resetSubscriberSelection()
      // Reset dialog mode to SELECTED so the bar disappears after bulk assign
      setDialogMode(SELECT_MODE.SELECTED)

      return response
    } catch (error) {
      const code = error?.response?.data?.code
      if (code === 608) {
        dispatch(
          MessageAlert.showError({
            message: formatMessage({
              id: 'duplicate.group.name.error.message',
            }),
          }),
        )
      }
      // Re-throw error so it can be handled in the calling component
      throw error
    }
  }

  const organizeInvitationsByStatus = () => {
    const result = {
      invited: [],
      pendingApprove: [],
    }

    if (!data.length) {
      return {
        invited: [],
        pendingApprove: [],
      }
    }
    const dataMap = new Map(data.map((item) => [item.id, item]))
    const pageSelectedIds = selectedIds[meta.currentPage]

    for (const id of pageSelectedIds || []) {
      const item = dataMap.get(id)
      if (!item) continue
      if (invitedStatus.includes(item.lastInvitationStatus)) {
        result.invited.push(id)
      } else if (pendingApprove.includes(item.lastInvitationStatus)) {
        result.pendingApprove.push(id)
      }
    }

    return result
  }

  const { fetchAllGroups } = useGroups()
  const {
    state: { params, isEmptySubscribers, isLoadingSubscribers, subscribers },
    handleSearch,
    handleGroups,
    handleFilter,
    handleResetFilter,
    handleFetchSubscribers,
    handleDeleteSubscriber,
    handleInviteSubscriber,
    handlePendingApproveSubscriber,
    handleMarkCorrect,
  } = useSubscribers({ resetSubscriberSelection })

  const { data, meta } = subscribers

  const onAction = async () => {
    await handleFetchSubscribers(params)
    setIsResetSelected(true)
  }

  useEffect(() => {
    setMultipleActionItems(() => {
      const newItems = organizeInvitationsByStatus()
      return {
        invited: [...newItems.invited],
        pendingApprove: [...newItems.pendingApprove],
      }
    })
  }, [selectedIds, meta.currentPage, data])

  const handleIndividualDialogAction = (
    type,
    data,
    source = ACTION_SOURCE_CHECKBOX,
  ) => {
    const subscriberIds = data
    const selectedIdsArray = selectedIds[meta.currentPage] || []

    switch (type) {
      case SubscriberMenuAction.DELETE: {
        let payload
        let count

        if (source === ACTION_SOURCE_ROW) {
          count = subscriberIds.length
          payload = mes_5173_crm_bulk_delete_subscribers
            ? {
                target: {
                  type: 'ids',
                  value: subscriberIds,
                },
              }
            : { ids: subscriberIds }
        } else {
          count = getSelectedCount()
          payload = mes_5173_crm_bulk_delete_subscribers
            ? buildBulkDeletePayload(
                dialogMode,
                selectedIdsArray,
                params,
                enableFilterByZipCode,
              )
            : { ids: subscriberIds }
        }

        return handleDeleteSubscriber(payload, onAction, count)
      }
      case SubscriberMenuAction.INVITE:
      case SubscriberMenuAction.REINVITE: {
        return handleInviteSubscriber({ ids: subscriberIds }, () =>
          setIsResetSelected(true),
        )
      }
      case SubscriberMenuAction.APPROVE: {
        return handlePendingApproveSubscriber(
          {
            ids: subscriberIds,
            placeId: place.id,
            status: PLACE_INVITATION_UNVERIFIED,
          },
          onAction,
        )
      }
      case SubscriberMenuAction.REJECT: {
        return handlePendingApproveSubscriber(
          {
            ids: subscriberIds,
            placeId: place.id,
            status: PLACE_INVITATION_DECLINED,
          },
          onAction,
        )
      }
      case SubscriberMenuAction.MARK_CORRECT: {
        return handleMarkCorrect({ subscriberId: data }, onAction)
      }
      default:
        return
    }
  }

  const handleOnClose = () => {
    if (isAddAnotherChecked) {
      handleResetFilter()
    }
    setIsSendInvitationChecked(false)
    setIsOpenOneByOne(false)
  }

  const formik = useFormik({
    initialValues: {
      search: '',
      filterGroups: { groups: [], tags: {} },
      filterStatus: [],
      filterSources: [],
      filterChannels: [],
      filterContactType: [],
      quickFilterZipCode: [],
    },
  })

  const handleResetTimeline = useCallback(() => {
    handleResetFilter()
    formik.resetForm()
    setIsInfo(false)
    resetSubscriberSelection()
    setDialogMode(SELECT_MODE.SELECTED)
  }, [formik, handleResetFilter, resetSubscriberSelection])

  const fetchFirstState = useCallback(() => {
    handleFetchSubscribers(params)
    if (isEmpty(groupList)) {
      fetchAllGroups()
    } else return
  }, [fetchAllGroups, groupList, handleFetchSubscribers, params])

  useEffect(() => {
    queryClient.invalidateQueries({
      queryKey: [SUBSCRIBER_GROUPS_QUERY_KEY],
    })
  }, [])

  const actionCreateSubscribers = () => {
    return (
      <AccessControl
        accesses={[AccessType.SUBSCRIBER_CREATE, AccessType.ADMINISTRATION]}
      >
        <OutlinedButtons
          id="create-subscriber-btn"
          color={SECONDARY}
          onClick={() => setIsOpenAddDialog(true)}
          className="w-full"
        >
          <div className="flex">
            <AddSubscribers classes="w-22 mr-3" />
            {formatMessage({
              id: 'subscriber.dialog.createSubscriber',
            })}
          </div>
        </OutlinedButtons>
      </AccessControl>
    )
  }

  const onSuccseAndRedirectPage = (res, scope) => {
    dispatch(
      MessageAlert.showSuccess({
        message: formatMessage({
          id: 'createNotification.success.message',
        }),
      }),
    )
    setTimeout(() => {
      const pathname =
        scope === SCOPE_BEACON
          ? Routes.beaconNotification()
          : Routes.notificationTimeline()
      history.push(pathname)
    }, 2000)
  }

  const onOpenCreateNotificationDialog = async () => {
    const currentSelected = selectedIds[meta.currentPage] || []
    const status = [
      STATUS_SUBSCRIBED_ACCEPT_REQUEST,
      STATUS_SUBSCRIBED_ACCEPT_INVITATION,
      STATUS_SUBSCRIBED_PUBLIC_SUBSCRIBE,
    ]
    const filteredData = data
      .filter(
        (item) =>
          currentSelected.includes(item.id) &&
          status.includes(item.lastInvitationStatus),
      )
      .map((item) => {
        item.name = `${item.firstName} ${item.lastName}`
        item.groupBy = SUBSCRIBERS
        item.type = SUBSCRIBERS

        return item
      })

    dispatch(
      openDialog({
        initialValues: isEmpty(filteredData)
          ? undefined
          : {
              subscribers: filteredData,
            },
        onCreateSuccess: onSuccseAndRedirectPage,
        type: GENERAL_DIALOG,
        action: ACTION_NEW,
      }),
    )
  }

  const handleSelectedRow = (data) => {
    history.push(Routes.viewSubscriberDetail(data.id))
  }

  const getSelectedCount = () => {
    const selectedIdsArray = selectedIds[meta.currentPage] || []
    if (dialogMode === SELECT_MODE.ALL) {
      return meta?.total || 0
    }
    return selectedIdsArray?.length || 0
  }

  const renderBulkActions = (handleIndividualDialogAction) => {
    const selectedIdsArray = selectedIds[meta.currentPage] || []
    const invitedList = multipleActionItems?.invited
    const approveList = multipleActionItems?.pendingApprove
    const invitedSelected = intersection(invitedList, selectedIdsArray)
    const approveSelected = intersection(approveList, selectedIdsArray)
    const selectedInvitedCount = invitedSelected.length
    const selectedPendingApproveCount = approveSelected.length
    const totalSelectedCount = selectedIdsArray.length

    const isSelectAllMode = dialogMode === SELECT_MODE.ALL
    const totalCount = meta?.total || 0
    const formattedTotalCount = totalCount.toLocaleString()
    const isSelectAllDisabled = totalCount > 3000 && !isSelectAllMode
    // Only show "Select all" link when all rows on current page are selected (select all checkbox clicked)
    const allRowsSelected =
      selectedIdsArray.length === data.length && data.length > 0

    return (
      <div className="flex items-center justify-between relative w-full">
        {/* Select all / Clear all selection button */}
        <div className="flex items-center grow gap-20">
          <SelectAllSubscriberButton
            allRowsSelected={allRowsSelected}
            isSelectAllDisabled={isSelectAllDisabled}
            isSelectAllMode={isSelectAllMode}
            formattedTotalCount={formattedTotalCount}
            handleToggleSelectAll={handleToggleSelectAll}
            formatMessage={formatMessage}
          />
          <BulkActionsButtonGroup
            selectedIdsArray={selectedIdsArray}
            isSelectAllMode={isSelectAllMode}
            getSelectedCount={getSelectedCount}
            openBulkAssignGroupDialog={openBulkAssignGroupDialog}
            mes_3841_show_crm_export={mes_3841_show_crm_export}
            mes_5173_crm_bulk_delete_subscribers={
              mes_5173_crm_bulk_delete_subscribers
            }
            selectedInvitedCount={selectedInvitedCount}
            handleIndividualDialogAction={handleIndividualDialogAction}
            invitedSelected={invitedSelected}
            totalSelectedCount={totalSelectedCount}
            selectedPendingApproveCount={selectedPendingApproveCount}
            approveSelected={approveSelected}
            formatMessage={formatMessage}
            selectedIds={selectedIds}
            meta={meta}
          />
        </div>
      </div>
    )
  }

  const handleSelectionChange = (newSelectedIds) => {
    const selectedIdsOfPage = newSelectedIds
    setSelectedIds(selectedIdsOfPage)

    // If in ALL mode and user manually changes selection (not all rows selected), revert to SELECTED mode
    if (dialogMode === SELECT_MODE.ALL) {
      const selectedIdsArray = selectedIdsOfPage[meta.currentPage] || []
      const allRowsSelected =
        selectedIdsArray.length === data.length && data.length > 0

      if (!allRowsSelected) {
        setDialogMode(SELECT_MODE.SELECTED)
      }
    }
  }

  const handlePerPageChange = () => {
    setDialogMode(SELECT_MODE.SELECTED)
    setSelectedIds({})
    setMultipleActionItems({ invited: [], pendingApprove: [] })
  }

  // Reset mode and selection when page changes (only if in ALL mode)
  const handlePageChange = () => {
    if (dialogMode === SELECT_MODE.ALL) {
      setDialogMode(SELECT_MODE.SELECTED)
      resetSubscriberSelection()
    }
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(fetchFirstState, [])

  useEffect(() => {
    if (isResetSelected) setIsResetSelected(false)
  }, [isResetSelected])

  // Set up ResizeObserver for combined filter + info container
  useEffect(() => {
    if (!headerRef.current || !window.ResizeObserver) return

    const resizeObserver = new ResizeObserver(() => {
      if (headerRef.current) {
        setFilterHeight(headerRef.current.offsetHeight)
      }
    })

    resizeObserver.observe(headerRef.current)
    return () => resizeObserver.disconnect()
  }, [])

  // Re-measure when filter values or info visibility change
  useEffect(() => {
    if (headerRef.current) {
      setFilterHeight(headerRef.current.offsetHeight)
    }
  }, [
    formik.values.filterGroups,
    formik.values.filterStatus,
    formik.values.filterSources,
    formik.values.filterChannels,
    formik.values.filterContactType,
    formik.values.quickFilterZipCode,
    enableFilterByZipCode,
    isInfo,
  ])

  return (
    <>
      <PanelPages
        isEmptyState={isEmptySubscribers}
        emptyState={
          <EmptyState
            title={formatMessage({
              id: 'subscribers.empty.state.title',
            })}
            description={formatMessage({
              id: 'subscribers.empty.state.description',
            })}
            accesses={[AccessType.SUBSCRIBER_CREATE, AccessType.ADMINISTRATION]}
            onAction={
              <div className="space-y-10">
                <AutoApprove />
                <div className="w-fit">{actionCreateSubscribers()}</div>
              </div>
            }
          />
        }
        title={formatMessage({
          id: 'navigation.directory.subscribers.label',
        })}
        rightContent={
          <div className="space-y-5 md:space-y-0 md:space-x-10 md:flex items-center">
            <FormControl variant="outlined" className="w-full md:w-256">
              <SimpleInput
                id="search-timeline-by-title"
                name="search"
                placeholder={formatMessage(
                  {
                    id: 'searchBar.placeholder.text',
                  },
                  {
                    subject: formatMessage({
                      id: 'navigation.directory.subscribers.label',
                    }),
                  },
                )}
                value={formik.values.search}
                fullWidth
                size="small"
                onChange={(event) => {
                  formik.setFieldValue('search', event.target.value)
                  handleSearch(event.target.value)
                }}
                startAdornment={
                  <InputAdornment position="start">
                    <SearchIcon className="text-grey-400" />
                  </InputAdornment>
                }
              />
            </FormControl>
            <div className="w-full md:w-fit">{actionCreateSubscribers()}</div>
            <ComposeMessageButton onClick={onOpenCreateNotificationDialog} />
          </div>
        }
      >
        <div ref={headerRef}>
          <PanelFilter
            leftContent={
              <>
                <GroupsSelector
                  name="filterGroups"
                  disabled={isLoadingSubscribers}
                  className="mt-5 mr-5 bg-primary-200 text-primary text-14 font-normal"
                  value={formik.values.filterGroups}
                  onChange={(value) => {
                    formik.setFieldValue('filterGroups', value)
                    handleGroups(value)
                  }}
                />
                <CheckboxSelectorV2
                  name="filterStatus"
                  label={formatMessage({ id: 'filter.statuses' })}
                  options={filterStatus}
                  value={formik.values.filterStatus}
                  disabled={isLoadingSubscribers}
                  onChange={(value) => {
                    formik.setFieldValue('filterStatus', value)
                    handleFilter(value, STATUS_FILTER)
                  }}
                  className="mr-5 bg-primary-200 text-primary text-14 font-normal"
                  isSelectAll={true}
                />
                <CheckboxSelectorV2
                  name="filterSources"
                  label={formatMessage({ id: 'filter.sources' })}
                  options={filterSource(appName)}
                  value={formik.values.filterSources}
                  disabled={isLoadingSubscribers}
                  onChange={(value) => {
                    formik.setFieldValue('filterSources', value)
                    handleFilter(value, SOURCE_FILTER)
                  }}
                  className="mr-5 bg-primary-200 text-primary text-14 font-normal"
                  isSelectAll={true}
                />
                <CheckboxSelectorV2
                  name="filterChannels"
                  label={formatMessage({ id: 'channels.label' })}
                  options={filterChannels(appName)}
                  value={formik.values.filterChannels}
                  disabled={isLoadingSubscribers}
                  onChange={(value) => {
                    formik.setFieldValue('filterChannels', value)
                    handleFilter(value, CHANNELS_FILTER)
                  }}
                  className="mr-5 bg-primary-200 text-primary text-14 font-normal"
                  isSelectAll={true}
                />
                <CheckboxSelectorV2
                  name="filterContactType"
                  label={formatMessage({ id: 'filter.contactTypes' })}
                  options={filterContactType}
                  value={formik.values.filterContactType}
                  disabled={isLoadingSubscribers}
                  onChange={(value) => {
                    formik.setFieldValue('filterContactType', value)
                    handleFilter(value, CONTACT_TYPE_FILTER)
                    setIsInfo(
                      value.some(({ value }) => value === CONTACT_TYPE_INVALID),
                    )
                  }}
                  className="mr-5 bg-primary-200 text-primary text-14 font-normal"
                  isSelectAll={true}
                />
                {enableFilterByZipCode && (
                  <CheckboxSelectorV2
                    name="quickFilterZipCode"
                    label={formatMessage({ id: 'filter.quickfilter' })}
                    options={filterZipCode}
                    value={formik.values.quickFilterZipCode}
                    disabled={isLoadingSubscribers}
                    onChange={(value) => {
                      formik.setFieldValue('quickFilterZipCode', value)
                      handleFilter(value, ZIP_CODE_FILTER)
                    }}
                    className="mr-5 bg-primary-200 text-primary text-14 font-normal"
                    isOneSelected={true}
                  />
                )}
                {formik.dirty ? (
                  <Button
                    className="p-0 text-primary text-14 font-bold normal-case disabled:text-grey-200"
                    onClick={handleResetTimeline}
                    disabled={isLoadingSubscribers}
                  >
                    {formatMessage({ id: 'text.clearAll' })}
                  </Button>
                ) : null}
              </>
            }
            rightContent={
              <div className="flex items-start h-full pt-2">
                <AutoApprove onFetch={() => handleFetchSubscribers(params)} />
              </div>
            }
          />
          {isInfo && (
            <PanelsInfo
              type={DISTRESS}
              className="my-5 flex space-x-3 items-center"
            >
              <Typography className="text-16 text-orange-600">
                {formatMessage({ id: 'subscribers.read.more.invalid.contact' })}
              </Typography>
              <OutlinedButtons
                id="view-detail-btn"
                color={WARNING}
                className="px-3 py-1 text-12"
                onClick={() => setIsReadMore(true)}
              >
                {formatMessage({
                  id: 'View Details',
                })}
              </OutlinedButtons>
            </PanelsInfo>
          )}
        </div>
        <DataTable
          data={data}
          isCheckbox={true}
          className={isInfo ? classes.tableWithInfo : classes.table}
          columns={ColumnOfData(
            formatMessage,
            isDownXs,
            handleIndividualDialogAction,
            enableFilterByZipCode,
          )}
          pagination={meta}
          sticky={isDownXs ? 'checkBox' : 'firstName'}
          isLoading={isLoadingSubscribers}
          isResetSelected={isResetSelected}
          isResetAllSelected={isResetAllSelected}
          setIsResetAllSelected={setIsResetAllSelected}
          onFetch={(value) => handleFetchSubscribers(mergeLeft(value, params))}
          onSelectedRow={(data) => handleSelectedRow(data)}
          activeRow={selectedRow?.id}
          onChange={(value) => handleSelectionChange(value)}
          bulkAction={renderBulkActions(handleIndividualDialogAction)}
          mode={dialogMode}
          onPerPageChange={handlePerPageChange}
          onPageChange={handlePageChange}
        />
        <InformationDialog
          open={isReadMore}
          onClose={() => setIsReadMore(false)}
          title={formatMessage({
            id: 'subscribers.dialog.badContact.title',
          })}
          className={classes.infoDialog}
        >
          <div className="my-5">
            <Typography className="text-14 text-primary mb-10">
              {formatMessage({
                id: 'subscribers.dialog.badContact.desescription',
              })}
            </Typography>
            <Typography className="text-14 text-primary font-bold">
              {`${formatMessage({
                id: 'subscribers.dialog.badContact.msg',
              })}:`}
            </Typography>
            <Typography className="text-14 text-primary mb-10">
              {formatMessage({
                id: 'subscribers.dialog.badContact.msg_1',
              })}
            </Typography>
            <Typography className="text-14 text-primary mb-10">
              <FormattedMessage
                id="subscribers.dialog.badContact.msg_2"
                values={{
                  b: (text) => <b>{text}</b>,
                }}
              />
            </Typography>
            <Typography className="text-14 text-primary mb-10">
              <FormattedMessage
                id="subscribers.dialog.badContact.msg_3"
                values={{
                  b: (text) => <b>{text}</b>,
                }}
              />
            </Typography>
            <Typography className="text-14 text-primary">
              <FormattedMessage
                id="subscribers.dialog.badContact.msg_4"
                values={{
                  b: (text) => <b>{text}</b>,
                }}
              />
            </Typography>
          </div>
        </InformationDialog>
      </PanelPages>
      <MenusAddSubscriber
        open={isOpenAddDialog}
        onClose={() => setIsOpenAddDialog(false)}
        openOneByOne={() => {
          setIsOpenAddDialog(false)
          setIsOpenOneByOne(true)
        }}
      />
      <AddOneByOne
        open={isOpenOneByOne}
        onClose={() => handleOnClose()}
        onFetch={() => handleResetFilter()}
        setIsAddAnotherChecked={setIsAddAnotherChecked}
        isSendInvitationChecked={isSendInvitationChecked}
        setIsSendInvitationChecked={setIsSendInvitationChecked}
      />
      <BulkAssignGroupDialog
        isOpen={isBulkAssignGroupDialogOpen}
        mode={dialogMode}
        subscriberIds={
          dialogMode === SELECT_MODE.SELECTED
            ? selectedIds[meta.currentPage] || []
            : []
        }
        subscriberCount={getSelectedCount()}
        value={[]}
        onConfirm={() => {}}
        onAssignGroups={handleBulkAssignGroups}
        onCloseDialog={closeDialog}
        params={params}
      />
    </>
  )
}

export default Subscribers
